
import { Box } from "@mui/material";
import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";

interface FastScrollThumbProps {
  targetRef?: RefObject<HTMLElement | null>;
  endBoundaryRef?: RefObject<HTMLElement | null>;
  contained?: boolean;
  minScrollablePx?: number;
  zIndex?: number;
  right?: number;
}

const WINDOW_TRACK_TOP = 12;
const WINDOW_TRACK_BOTTOM = 12;
const SHEET_TRACK_INSET = 10;
const TECHNICAL_OVERFLOW_TOLERANCE = 12;
const MIN_THUMB_HEIGHT = 48;
const MAX_THUMB_HEIGHT = 108;

function openModalRoots(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(".MuiModal-root")).filter((element) => {
    if (element.getAttribute("aria-hidden") === "true") return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  });
}

function scrollbarBelongsToTopLayer(target: HTMLElement | null): boolean {
  const modals = openModalRoots();
  if (modals.length === 0) return true;
  if (!target) return false;
  const owner = target.closest<HTMLElement>(".MuiModal-root");
  return owner === modals[modals.length - 1];
}

export default function FastScrollThumb({
  targetRef,
  endBoundaryRef,
  contained = false,
  minScrollablePx = 1,
  zIndex = 1450,
  right = 2,
}: FastScrollThumbProps) {
  const [visible, setVisible] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [thumbTop, setThumbTop] = useState(WINDOW_TRACK_TOP);
  const [thumbHeight, setThumbHeight] = useState(72);
  const pointerOffset = useRef(0);
  const metricsRef = useRef({
    trackTop: WINDOW_TRACK_TOP,
    maxTop: WINDOW_TRACK_TOP,
    maxScroll: 0,
  });

  useEffect(() => {
    const getTarget = (): HTMLElement | null => targetRef?.current ?? null;

    const update = () => {
      const target = getTarget();

      // Seiten dürfen einen inhaltlichen Root markieren. Wenn dieser Root vollständig
      // in den aktuellen Viewport passt, unterdrücken wir den globalen Fast-Scroll.
      // Damit erzeugt technisches Container-Padding keinen scheinbaren Scrollbedarf.
      if (!target) {
        const autoRoot = document.querySelector<HTMLElement>('[data-fast-scroll-auto="true"]');
        if (autoRoot) {
          const rect = autoRoot.getBoundingClientRect();
          const availableBottom = window.innerHeight - 8;
          if (rect.top >= -2 && rect.bottom <= availableBottom + 2) {
            setVisible(false);
            return;
          }
        }
      }

      // Es darf immer nur der Scrollbalken der obersten sichtbaren Ebene
      // erscheinen. Damit verschwindet der Seiten-Fast-Scroll sobald ein
      // Dialog/Sheet geöffnet ist und bei verschachtelten Sheets bleibt nur
      // der Scrollbalken des obersten Popups sichtbar.
      if (!scrollbarBelongsToTopLayer(target)) {
        setVisible(false);
        return;
      }

      let viewportHeight: number;
      let contentHeight: number;
      let scrollTop: number;
      let trackTop: number;
      let trackBottom: number;

      if (target) {
        const targetRect = target.getBoundingClientRect();

        viewportHeight = target.clientHeight;
        contentHeight = target.scrollHeight;
        scrollTop = target.scrollTop;

        if (contained) {
          // Contained-Modus: Trackkoordinaten sind relativ zum eigenen
          // Scrollcontainer. Dadurch kann der Thumb niemals in einen
          // außerhalb liegenden Griff-/Footerbereich hineinragen.
          trackTop = SHEET_TRACK_INSET;
          trackBottom = Math.max(
            SHEET_TRACK_INSET,
            window.innerHeight - viewportHeight + SHEET_TRACK_INSET
          );
        } else {
          trackTop = Math.max(
            SHEET_TRACK_INSET,
            targetRect.top + SHEET_TRACK_INSET
          );
          const boundaryTop = endBoundaryRef?.current
            ? endBoundaryRef.current.getBoundingClientRect().top
            : targetRect.bottom;
          const visibleTargetBottom = Math.min(targetRect.bottom, boundaryTop);

          trackBottom = Math.max(
            SHEET_TRACK_INSET,
            window.innerHeight - visibleTargetBottom + SHEET_TRACK_INSET
          );
        }
      } else {
        const doc = document.documentElement;
        viewportHeight = window.innerHeight;
        contentHeight = Math.max(doc.scrollHeight, document.body.scrollHeight);
        scrollTop = window.scrollY;
        trackTop = WINDOW_TRACK_TOP;
        trackBottom = WINDOW_TRACK_BOTTOM;
      }

      const maxScroll = Math.max(0, contentHeight - viewportHeight);
      const visibilityThreshold = Math.max(
        minScrollablePx,
        TECHNICAL_OVERFLOW_TOLERANCE
      );
      const trackHeight = Math.max(
        84,
        contained
          ? viewportHeight - SHEET_TRACK_INSET * 2
          : window.innerHeight - trackTop - trackBottom
      );
      const proportionalThumb =
        trackHeight * (viewportHeight / Math.max(contentHeight, viewportHeight));
      const nextThumbHeight = Math.max(
        MIN_THUMB_HEIGHT,
        Math.min(MAX_THUMB_HEIGHT, proportionalThumb)
      );
      const maxTop = trackTop + trackHeight - nextThumbHeight;
      const ratio = maxScroll > 0 ? scrollTop / maxScroll : 0;

      metricsRef.current = { trackTop, maxTop, maxScroll };
      setVisible(maxScroll > visibilityThreshold);
      setThumbHeight(nextThumbHeight);

      if (!dragging) {
        setThumbTop(trackTop + ratio * Math.max(0, maxTop - trackTop));
      }
    };

    update();

    const target = getTarget();
    const scrollTarget: EventTarget = target ?? window;
    scrollTarget.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);

    const resizeObserver = new ResizeObserver(update);
    if (target) {
      resizeObserver.observe(target);
      if (target.firstElementChild instanceof HTMLElement) {
        resizeObserver.observe(target.firstElementChild);
      }
    } else {
      resizeObserver.observe(document.body);
      resizeObserver.observe(document.documentElement);
    }

    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "aria-hidden", "aria-modal"],
    });

    return () => {
      scrollTarget.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [contained, dragging, endBoundaryRef, minScrollablePx, targetRef]);

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    const targetTop =
      contained && targetRef?.current
        ? targetRef.current.getBoundingClientRect().top
        : 0;
    pointerOffset.current = event.clientY - targetTop - thumbTop;
    setDragging(true);
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!dragging) return;

    const { trackTop, maxTop, maxScroll } = metricsRef.current;
    const targetTop =
      contained && targetRef?.current
        ? targetRef.current.getBoundingClientRect().top
        : 0;
    const pointerY = event.clientY - targetTop;
    const nextTop = Math.max(
      trackTop,
      Math.min(maxTop, pointerY - pointerOffset.current)
    );

    setThumbTop(nextTop);

    const ratio =
      maxTop > trackTop
        ? (nextTop - trackTop) / (maxTop - trackTop)
        : 0;

    if (targetRef?.current) {
      targetRef.current.scrollTo({ top: ratio * maxScroll, behavior: "auto" });
    } else {
      window.scrollTo({ top: ratio * maxScroll, behavior: "auto" });
    }
  }

  function stopDrag() {
    setDragging(false);
  }

  if (!visible) return null;

  return (
    <Box
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      aria-label="Schnellscrollen"
      role="scrollbar"
      sx={{
        position: contained ? "absolute" : "fixed",
        right,
        top: thumbTop,
        zIndex,
        width: 22,
        height: thumbHeight,
        touchAction: "none",
        cursor: dragging ? "grabbing" : "grab",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        pr: "3px",
        opacity: dragging ? 1 : 0.74,
        transition: dragging ? "none" : "opacity 160ms ease",
        "&::after": {
          content: '""',
          width: 6,
          height: "100%",
          borderRadius: 99,
          bgcolor: "#83cee0",
          boxShadow: "0 0 0 1px rgba(0,0,0,0.32)",
        },
      }}
    />
  );
}
